import compact from "lodash.compact";

// Local Algolia shape — narrower than @algolia/client-search's MultipleQueriesResponse
// but wide enough for what this composable actually consumes.
interface AlgoliaHit {
   id?: number | string;
   title?: string;
   objectID?: string;
   type?: string;
   teachable_type?: string;
   discipline?: number | string | null;
   url?: string;
   image?: unknown;
   slug?: string;
   sub_title?: string;
   _highlightResult?: {
      title?: { value?: string };
      teachable_type?: { value?: string };
   };
   intended_ages?: string;
}
interface AlgoliaGroup {
   index: string;
   hits: AlgoliaHit[];
}
interface SearchResultGroup {
   type: string;
   hits: AlgoliaHit[];
}

const INDEX_RESOURCE_TYPES: Record<string, string> = {
   teachables: "teachable",
   core_works: "core-work",
   unit_sequences: "planning-tool",
   artistic_insights: "artistic_insight",
};

const MODEL_RESOURCE_TYPES: Record<string, string> = {
   Teachable: "teachable",
   CoreWork: "core-work",
   UnitSequence: "planning-tool",
   ArtisticInsight: "artistic_insight",
};

const resourceTypeFromObjectID = (objectID?: string) => {
   const model = objectID?.match(/([^\\:]+)::/)?.[1];

   return model ? MODEL_RESOURCE_TYPES[model] : undefined;
};

const resourceTypeForHit = (hit: AlgoliaHit, index: string) =>
   hit.teachable_type ??
   hit._highlightResult?.teachable_type?.value ??
   resourceTypeFromObjectID(hit.objectID) ??
   INDEX_RESOURCE_TYPES[index] ??
   hit.type;

export function useGlobalSearch() {
   const { log, logInfo } = useDebugger();
   const { $algolia } = useNuxtApp() as unknown as {
      $algolia: { search: (q: unknown[]) => Promise<{ results: AlgoliaGroup[] }> };
   };

   const { buildFacet, filterCards } = useAlgolia();

   const authStore = useAuthStore();
   const { getUserDisciplines } = storeToRefs(authStore);
   const { user } = storeToRefs(authStore);

   const hitLimit = 1000;

   // >> STATE
   const searchResults = useState<Array<SearchResultGroup | false>>(
      "searchResults",
      () => [],
   );
   const searchDisciplines = useState(
      "searchDisciplines",
      (): Array<number> | number => getUserDisciplines.value as Array<number>,
   );
   const searchQuery = useState("searchQuery", (): string => "");
   const searchHits = useState<number>("searchHits", () => 0);

   //  >> INTERFACE
   interface queryInterface {
      query: string;
      limit?: number;
   }
   // >>COMPUTED
   const getDisciplines = computed(() => searchDisciplines.value);
   const getSearchResults = computed(() => searchResults.value);

   /**
    * The `search` function performs a search using Algolia based on the current query across all indices.
    *
    * @returns {Promise<void>} - A promise that resolves when the search is complete.
    */
   const search = async ({
      query = searchQuery.value,
      limit = 100,
   }: queryInterface) => {
      //set global query
      searchQuery.value = query;
      logInfo("GlobalSearch::SearchQuery", query);
      // Perform the search using the appropriate Algolia index based on the order_by filter.
      // const results = await algoliaSearch();
      const indices = [
         "teachables",
         "core_works",
         "unit_sequences",
         "artistic_insights",
         "help_center_topics",
      ];

      const searchQueries = indices.map((indexName) => ({
         indexName,
         query: query,
         params: {
            hitsPerPage: limit,
            getRankingInfo: true,
            facetFilters: [
               buildFacet(searchDisciplines.value, "discipline_id"),
            ],
         },
      }));

      const { results } = await $algolia.search(searchQueries);

      log("RAW SearchResults", results);
      searchHits.value = results.reduce(
         (acc: number, result) => result.hits.length + acc,
         0,
      );

      // Map to new schema. Use `false` for empty groups so compact() can drop them.
      searchResults.value = results.map((group): SearchResultGroup | false =>
         group.hits.length
            ? {
                 type: group.index,
                 hits: group.hits.slice(0, hitLimit).map((hit) => ({
                    ...hit,
                    type: resourceTypeForHit(hit, group.index),
                    id: hit.id,
                    hightlightedTitle:
                       hit._highlightResult?.title?.value ?? hit.title,
                    discipline: hit.discipline,
                    url: hit.url,
                    image: hit.image,
                    slug: hit.slug,
                    sub_title: hit.sub_title,
                 })) as AlgoliaHit[],
              }
            : false,
      );
      // Filter age-gated cards.
      const userValue = user.value;
      const ageRestrictions =
         userValue && "school" in userValue
            ? userValue.school?.age_restrictions
            : undefined;
      const ages = ageMapper(ageRestrictions);
      searchResults.value = searchResults.value.map((group) => {
         if (group && group.hits && ages.length) {
            group.hits = filterCards(
               group.hits as unknown as Record<string, unknown>[],
               userValue!,
               ages,
            ) as unknown as AlgoliaHit[];
         }
         return group;
      });
      searchResults.value = compact(searchResults.value);
      return searchResults.value;
   };

   const route = useRoute();
   //>> SETTERS
   const setDisciplines = (ids: number | Array<number>): void => {
      searchDisciplines.value = ids;
      search({ query: searchQuery.value ?? route?.query?.search });
   };

   const clearSearch = () => {
      searchDisciplines.value = getUserDisciplines.value as Array<number>;
      searchResults.value = [];
      searchQuery.value = "";
      
   };

   return {
      searchResults: getSearchResults,
      searchQuery,
      searchHits,
      getDisciplines,
      search,
      clearSearch,
      setDisciplines,
   };
}
